import Cookie from 'universal-cookie';
import { POST } from '../Screens/dataSaver';

export const COMPLY_SHIELD_RELAY_KEY = 'complyShieldRelayState';

let codeExchangePromise = null;
let codeExchangeState = 'idle';

const authorizationCookieOptions = () => {
  const hostname = window.location.hostname;
  return {
    path: '/',
    domain: hostname?.split('.')?.length >= 3
      ? hostname.split('.').slice(-2).join('.')
      : hostname,
    secure: true,
    sameSite: 'none',
  };
};

/**
 * HapiCare launch state from the SSO callback.
 * Descope's own `relayState` / `RelayState` is a different value and must not be used here.
 */
export const captureComplyShieldRelayState = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const complyshieldRelayState = params.get('complyshieldRelayState');
    if (complyshieldRelayState) {
      sessionStorage.setItem(COMPLY_SHIELD_RELAY_KEY, complyshieldRelayState);
    }
  } catch (error) {
    console.warn('Failed to capture ComplyShield relay state', error);
  }
};

/**
 * Stay on the loader while the authorization code is being exchanged
 * or while the HapiCare login API has not finished yet.
 */
export const shouldWaitForComplyShieldSso = () => {
  if (codeExchangeState === 'failed') return false;
  if (codeExchangeState === 'pending') return true;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const complyshieldRelayState = params.get('complyshieldRelayState')
      || sessionStorage.getItem(COMPLY_SHIELD_RELAY_KEY);
    if (code && complyshieldRelayState) return true;
    const cookie = new Cookie();
    if (complyshieldRelayState && cookie.get('authorization') && !cookie.get('user')) {
      return true;
    }
  } catch (error) {
    return false;
  }
  return false;
};

/**
 * `code` is a one-time Descope authorization code, not a session token.
 * Exchange it with the Descope SAML API, then store the returned sessionJwt.
 */
export const exchangeDescopeAuthorizationCode = (descopeSdk) => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const complyshieldRelayState = params.get('complyshieldRelayState')
    || sessionStorage.getItem(COMPLY_SHIELD_RELAY_KEY);
  if (!code || !complyshieldRelayState || !descopeSdk?.saml?.exchange) {
    return Promise.resolve(null);
  }
  if (codeExchangePromise) return codeExchangePromise;

  codeExchangeState = 'pending';
  codeExchangePromise = (async () => {
    captureComplyShieldRelayState();
    const nextParams = new URLSearchParams(window.location.search);
    nextParams.delete('code');
    const search = nextParams.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash || ''}`
    );

    const resp = await descopeSdk.saml.exchange(code);
    const sessionJwt = resp?.data?.sessionJwt;
    if (!resp?.ok || !sessionJwt) {
      const message = resp?.error?.errorDescription
        || resp?.error?.errorMessage
        || 'Descope authorization code exchange failed';
      throw new Error(message);
    }

    const cookie = new Cookie();
    cookie.set('authorization', sessionJwt, authorizationCookieOptions());
    codeExchangeState = 'done';
    return sessionJwt;
  })().catch((error) => {
    codeExchangeState = 'failed';
    throw error;
  });

  return codeExchangePromise;
};

/**
 * If a RelayState was saved from SureShield SAML, resolve the P&P launch URL and redirect.
 * @returns {Promise<boolean>} true if navigation to P&P was started; false to continue normal redirect.
 */
export const tryComplyShieldSsoRedirect = async () => {
  const relayState = sessionStorage.getItem(COMPLY_SHIELD_RELAY_KEY);
  if (!relayState) return false;

  try {
    const response = await POST(
      'user-management-service/auth/complyshield/sso/resolve',
      { relayState }
    );
    const launchUrl = String(response?.data?.launchUrl || '')
      .trim()
      .replace(/^\//, '');

    if (!launchUrl) {
      sessionStorage.removeItem(COMPLY_SHIELD_RELAY_KEY);
      return false;
    }

    sessionStorage.removeItem(COMPLY_SHIELD_RELAY_KEY);
    window.location.replace(`/${launchUrl}`);
    return true;
  } catch (error) {
    console.warn(
      'ComplyShield SSO resolve failed; falling back to normal redirect',
      error
    );
    sessionStorage.removeItem(COMPLY_SHIELD_RELAY_KEY);
    return false;
  }
};
